import { isNumber } from 'lodash';
import React, { useEffect, useState } from 'react';
import { SliderInput } from '@meitu/saas-ui';
import { hexToHsva, hsvaToHexa } from '@meitu/saas-colors/dist/esm/utils/color-convert';
import { type TextLayer } from '@meitu/saas-editor-core/es/layers/text-layer';
import { type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { EffectPropsContainer } from '@/components/effect-props-container';
import LabelAction from '@/components/label-action';
import { useEditor } from '@/hooks/use-editor';
import { useTextRange } from '@/hooks/use-text-range-attrs-state';
import { useLocaleStore } from '@/i18n';
import { useLayerProps } from '../../hooks';
import { type WidgetCommonProps } from '../../types';
import { Tracker } from '../../utils';
import { TextColorPicker } from '../text-color-picker';

type Props = WidgetCommonProps;

export const FillInspector: React.FC<Props> = ({ layerId, recordId, ...restProps }) => {
    const { $t } = useLocaleStore();

    const editor = useEditor();
    const textRange = useTextRange(layerId);
    const props = useLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        const { color, texture, textGradient, textContents } = layerData;

        return {
            color,
            texture,
            textGradient,
            textContents,
        };
    });

    const color = props ? props.color : '#000000ff';
    const texture = props ? props.texture : null;
    const textGradient = props ? props.textGradient : null;
    const hasRichText = props && props.textContents && props.textContents.length > 0;

    const [fillOpacity, setFillOpacity] = useState(() => {
        const hsva = hexToHsva(color);
        return Math.round(hsva.a * 100);
    });

    const hasTextSelection = !!textRange && textRange.start !== textRange.end;
    const disableFillOpacity =
        hasTextSelection || hasRichText || texture != null || (textGradient != null && textGradient.colors.length > 1);

    const getColorWithOpacity = (opacity: number) => hsvaToHexa({ ...hexToHsva(color), a: opacity / 100 });

    const handleChange = (value: number) => {
        if (!isNumber(value)) return;

        const finalValue = Math.round(value);
        setFillOpacity(finalValue);

        editor
            .getInstance<TextLayer>('TextLayer')
            .setFillColor(layerId, getColorWithOpacity(finalValue), { writeHistory: false });
    };

    const handleAfterChange = (value: number) => {
        if (!isNumber(value)) return;

        const finalValue = Math.round(value);
        setFillOpacity(finalValue);

        editor.getInstance<TextLayer>('TextLayer').setFillColor(layerId, getColorWithOpacity(finalValue), {
            writeHistory: true,
        });

        Tracker.setFillOpacity({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            opacity: finalValue,
            color,
            panelPosition: 'secondary',
        });
    };

    useEffect(() => {
        if (disableFillOpacity) return;

        const hsva = hexToHsva(color);
        setFillOpacity(Math.round(hsva.a * 100));
    }, [color, disableFillOpacity]);

    return (
        <EffectPropsContainer icon="PaintBucketBoldFill" title={$t((v) => v.textStyleAdd.fillOrGradient)}>
            <div className="text-fill-inspector">
                {/* 填充色 */}
                <LabelAction title={$t((v) => v.textStyleAdd.color)} align>
                    <TextColorPicker
                        useForFill
                        size={{ width: 58, height: 26 }}
                        panelPosition="secondary"
                        {...props}
                        layerId={layerId}
                        recordId={recordId}
                        {...restProps}
                    />
                </LabelAction>

                {/* 透明度 */}
                {!disableFillOpacity ? (
                    <LabelAction title={$t((v) => v.textStyleAdd.opacity)} align>
                        <SliderInput
                            showStepBtn
                            value={fillOpacity}
                            onChange={(value) => handleChange(value)}
                            onAfterChange={(value) => handleAfterChange(value)}
                            min={0}
                            max={100}
                            step={1}
                            precision={1}
                            type="horizontal"
                        />
                    </LabelAction>
                ) : null}
            </div>
        </EffectPropsContainer>
    );
};
